import { useTranslations } from 'gt-react'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  EyeOff,
  Plus,
  Search,
  Table,
  Trash2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { type RecordSearchResult, useRecordNav } from './record-nav-context'

/**
 * FileMaker-style record navigator rendered in the header (center, right of the
 * sidebar trigger). Visible only when a detail page has published a config.
 */
export function RecordNav() {
  const t = useTranslations()
  const { nav } = useRecordNav()
  if (!nav) return null

  // Lightweight variant for overview lists: count + in-place list filter + new.
  if (nav.kind === 'list') {
    return (
      <div className="hidden lg:flex items-center gap-0.5">
        <span className="mx-1 h-5 w-px bg-border" />
        <span className="px-2 text-xs tabular-nums text-muted-foreground whitespace-nowrap">
          {t('packaging.recordNav.records', { count: nav.total })}
        </span>
        <span className="mx-1 h-5 w-px bg-border" />
        {nav.find ? (
          <FindButton onClick={nav.find} />
        ) : nav.onSearch ? (
          <ListNavFilter onSearch={nav.onSearch} />
        ) : null}
        {nav.create ? (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={nav.create}>
            <Plus className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <div className="hidden lg:flex items-center gap-0.5">
      <span className="mx-1 h-5 w-px bg-border" />
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        disabled={!nav.goFirst}
        onClick={() => nav.goFirst?.()}
      >
        <ChevronsLeft className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        disabled={!nav.goPrev}
        onClick={() => nav.goPrev?.()}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="px-2 text-xs tabular-nums text-muted-foreground whitespace-nowrap">
        {nav.position} / {nav.total}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        disabled={!nav.goNext}
        onClick={() => nav.goNext?.()}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        disabled={!nav.goLast}
        onClick={() => nav.goLast?.()}
      >
        <ChevronsRight className="h-4 w-4" />
      </Button>
      <span className="mx-1 h-5 w-px bg-border" />
      {nav.find ? (
        <FindButton onClick={nav.find} />
      ) : nav.searchRecords ? (
        <RecordNavSearch search={nav.searchRecords} onPick={nav.goTo} />
      ) : nav.onSearch ? (
        <ListNavFilter onSearch={nav.onSearch} />
      ) : null}
      {nav.omitFromFound ? (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          title={t('findMode.omitFromFound')}
          aria-label={t('findMode.omitFromFound')}
          onClick={nav.omitFromFound}
        >
          <EyeOff className="h-4 w-4" />
        </Button>
      ) : null}
      {nav.toList ? (
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={nav.toList}>
          <Table className="h-4 w-4" />
        </Button>
      ) : null}
      {nav.create ? (
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={nav.create}>
          <Plus className="h-4 w-4" />
        </Button>
      ) : null}
      {nav.remove ? (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-red-600 hover:text-red-600"
          onClick={nav.remove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  )
}

/** Het vergrootglas van de zoekmodus (spec 2026-10-21-zoekmodus §3): één zoekfunctie. */
function FindButton({ onClick }: { onClick: () => void }) {
  const t = useTranslations()
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8"
      title={t('findMode.find')}
      aria-label={t('findMode.find')}
      onClick={onClick}
    >
      <Search className="h-4 w-4" />
    </Button>
  )
}

/** Overview-list filter: narrows the table in place as you type (no navigation). */
function ListNavFilter({ onSearch }: { onSearch: (term: string) => void }) {
  const [term, setTerm] = useState('')
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
      <Input
        value={term}
        onChange={(e) => {
          setTerm(e.target.value)
          onSearch(e.target.value)
        }}
        className="h-8 w-48 pl-7"
      />
    </div>
  )
}

/** In-table record search popover: debounced query of the current entity only. */
function RecordNavSearch({
  search,
  onPick,
}: {
  search: (term: string) => Promise<RecordSearchResult[]>
  onPick: (id: string) => void
}) {
  const t = useTranslations()
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const [results, setResults] = useState<RecordSearchResult[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    const q = term.trim()
    if (!q) {
      setResults([])
      setLoading(false)
      return
    }
    setLoading(true)
    let cancelled = false
    const handle = setTimeout(() => {
      search(q)
        .then((r) => !cancelled && setResults(r))
        .catch(() => !cancelled && setResults([]))
        .finally(() => !cancelled && setLoading(false))
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [term, open, search])

  const reset = () => {
    setTerm('')
    setResults([])
    setLoading(false)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) reset()
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Search className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <div className="p-2">
          <Input autoFocus value={term} onChange={(e) => setTerm(e.target.value)} className="h-8" />
        </div>
        <div className="max-h-72 overflow-y-auto border-t">
          {loading ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">…</p>
          ) : results.length > 0 ? (
            results.map((r) => (
              <button
                key={r.id}
                type="button"
                className="flex w-full flex-col items-start px-3 py-1.5 text-left text-sm hover:bg-accent"
                onClick={() => {
                  onPick(r.id)
                  setOpen(false)
                  reset()
                }}
              >
                <span className="font-medium">{r.label}</span>
                {r.sublabel ? (
                  <span className="text-xs text-muted-foreground">{r.sublabel}</span>
                ) : null}
              </button>
            ))
          ) : term.trim() ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              {t('packaging.recordNav.noResults')}
            </p>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  )
}
